import { CLOCK_EVENT_ACTION, CLOCK_EVENT_MODE } from '../../../common/constants';

export const CLOCK_PASS_COPY = {
	TITLE: 'Shift pass',
	RESTRICTED_TEXT: 'Shift pass is not available for this account.',
	SUBTITLE: 'Scan this at the kiosk to clock in or out.',
	GENERATING_CODE: 'Generating secure code...',
	REFRESHING_CODE: 'Refreshing code...',
	REFRESH_CODE_BUTTON: 'Refresh code',
	ERROR_GENERATE_QR: 'Unable to generate clock QR code',
	DEFAULT_ACTION_LABEL: 'Clock',
	EXPIRES_SUFFIX: 'code expires in',
	ACTION_SELECTOR_CLOCK_IN: 'Clock in',
	ACTION_SELECTOR_CLOCK_OUT: 'Clock out',
	TAP_TO_ENLARGE: 'Tap the code to enlarge',
	FULLSCREEN_CLOSE_BUTTON: 'Close',
	SCAN_FEEDBACK_SAVED: 'saved',
	SCAN_FEEDBACK_SAVED_AT: 'saved at',
	SCAN_FEEDBACK_SOURCE_EXTERNAL: 'Deputy admin update',
	LAST_SCAN_LABEL: 'Last scan',
	LAST_SCAN_EMPTY: 'No scan yet',
	SHIFT_SUMMARY_TITLE: 'Shift summary',
	SHIFT_SUMMARY_DURATION_LABEL: 'Worked',
	SHIFT_SUMMARY_DURATION_EMPTY: 'Not available yet',
	SHIFT_SUMMARY_CLOCK_IN_LABEL: 'Clocked in',
	SHIFT_SUMMARY_CLOCK_OUT_LABEL: 'Clocked out',
	SHIFT_SUMMARY_TIME_EMPTY: '-',
	SHIFT_SUMMARY_HINT_NO_CLOCK_IN: 'Clock-out saved. Matching clock-in is not available yet.',
	SHIFT_SUMMARY_CLOSE_BUTTON: 'Done',
};

export const CLOCK_PASS_SCAN_FEEDBACK = {
	VIBRATION_MS: 120,
	TIME_LABEL_OPTIONS: {
		hour: '2-digit',
		minute: '2-digit',
	},
};

export const CLOCK_PASS_SCAN_POLL = {
	// Subscription remains the primary real-time path; polling is only a safety net.
	INTERVAL_MS: 3000,
};

export const CLOCK_PASS_QR_PREVIEW = {
	CLOSE_BUTTON_MARGIN_TOP: 12,
	CLOSE_BUTTON_RIGHT_OFFSET: 22,
};

const CLOCK_PASS_SHIFT_SUMMARY = {
	TIME_LABEL_OPTIONS: {
		hour: '2-digit',
		minute: '2-digit',
	},
	MINUTES_IN_HOUR: 60,
};

export const getSecondsUntil = (expiresAt) => {
	if (!expiresAt) {
		return 0;
	}

	const expiresAtMs = new Date(expiresAt).getTime();
	if (Number.isNaN(expiresAtMs)) {
		return 0;
	}

	return Math.max(0, Math.ceil((expiresAtMs - Date.now()) / 1000));
};

const getCapturedAtTimeLabel = (capturedAt) => {
	if (!capturedAt) {
		return '';
	}

	const capturedAtDate = new Date(capturedAt);
	if (Number.isNaN(capturedAtDate.getTime())) {
		return '';
	}

	return capturedAtDate.toLocaleTimeString([], CLOCK_PASS_SCAN_FEEDBACK.TIME_LABEL_OPTIONS);
};

const isExternalClockEventSource = (clockEvent) => {
	const numericMode = Number(clockEvent?.mode);
	if (numericMode === CLOCK_EVENT_MODE.EXTERNAL) {
		return true;
	}

	const normalizedModeName = String(clockEvent?.modeName || '').trim().toLowerCase();
	return normalizedModeName === 'external';
};

const appendClockEventSourceLabel = ({
	clockEvent,
	message,
}) => {
	const baseMessage = String(message || '').trim();
	if (!baseMessage) {
		return baseMessage;
	}

	if (!isExternalClockEventSource(clockEvent)) {
		return baseMessage;
	}

	return `${baseMessage} (${CLOCK_PASS_COPY.SCAN_FEEDBACK_SOURCE_EXTERNAL})`;
};

export const buildClockScanFeedbackMessage = (clockEvent) => {
	const actionName = String(clockEvent?.actionName || '').trim();
	const capturedAtTimeLabel = getCapturedAtTimeLabel(clockEvent?.capturedAt);
	let feedbackMessage = '';

	if (actionName && capturedAtTimeLabel) {
		feedbackMessage = `${actionName} ${CLOCK_PASS_COPY.SCAN_FEEDBACK_SAVED_AT} ${capturedAtTimeLabel}`;
	}
	else if (actionName) {
		feedbackMessage = `${actionName} ${CLOCK_PASS_COPY.SCAN_FEEDBACK_SAVED}`;
	}

	return appendClockEventSourceLabel({
		clockEvent,
		message: feedbackMessage,
	});
};

export const getClockActionFromLabel = (actionLabelValue) => {
	const normalizedActionLabel = String(actionLabelValue || '').trim().toLowerCase();
	if (!normalizedActionLabel) {
		return null;
	}

	if (normalizedActionLabel.includes('out')) {
		return CLOCK_EVENT_ACTION.CLOCK_OUT;
	}

	if (normalizedActionLabel.includes('in')) {
		return CLOCK_EVENT_ACTION.CLOCK_IN;
	}

	return null;
};

export const getShiftSummaryTimeLabel = (value) => {
	if (!value) {
		return CLOCK_PASS_COPY.SHIFT_SUMMARY_TIME_EMPTY;
	}

	const dateValue = value instanceof Date ? value : new Date(value);
	if (Number.isNaN(dateValue.getTime())) {
		return CLOCK_PASS_COPY.SHIFT_SUMMARY_TIME_EMPTY;
	}

	return dateValue.toLocaleTimeString([], CLOCK_PASS_SHIFT_SUMMARY.TIME_LABEL_OPTIONS);
};

export const getShiftSummaryDurationLabel = (workedMinutesValue) => {
	const workedMinutes = Number(workedMinutesValue);
	if (!Number.isFinite(workedMinutes) || workedMinutes < 0) {
		return CLOCK_PASS_COPY.SHIFT_SUMMARY_DURATION_EMPTY;
	}

	const roundedMinutes = Math.round(workedMinutes);
	const workedHours = Math.floor(roundedMinutes / CLOCK_PASS_SHIFT_SUMMARY.MINUTES_IN_HOUR);
	const remainingMinutes = roundedMinutes % CLOCK_PASS_SHIFT_SUMMARY.MINUTES_IN_HOUR;

	if (!workedHours) {
		return `${remainingMinutes}m`;
	}

	if (!remainingMinutes) {
		return `${workedHours}h`;
	}

	return `${workedHours}h ${remainingMinutes}m`;
};
